import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    // rg: cerrar por "salir del <li>" es fragil -- el submenu se anima abriendose
    // (su altura real tarda un momento en llegar a su valor final) y ademas es
    // mas ancho que el link que lo dispara, asi que el mouse real cruza zonas
    // que geometricamente ya no son el <li> antes de llegar al contenido. En vez
    // de perseguir cada caso, cerramos solo cuando el mouse sale de TODO el
    // componente del menu (el <header-menu> completo, header + submenu
    // desplegado) -- el patron estandar en menus desplegables con mega-menu.
    this.addEventListener('pointerleave', this.#menuPointerLeaveListener);
    // rg (05-10): en escritorio el submenu se abre al HACER CLIC y queda fijo hasta elegir una opcion
    // (como en el celular). Antes se abria al pasar el mouse y se cerraba al ir a elegir una opcion, y el
    // clic en "Packs en oferta" navegaba directo a su primera opcion.
    this.addEventListener('click', this.#clickListener, true);
    document.addEventListener('click', this.#outsideClickListener);
    document.addEventListener('keydown', this.#escListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('pointerleave', this.#menuPointerLeaveListener);
    this.removeEventListener('click', this.#clickListener, true);
    document.removeEventListener('click', this.#outsideClickListener);
    document.removeEventListener('keydown', this.#escListener);
    this.#cleanupMutationObserver();
    this.#cancelScheduledAction();
  }

  /**
   * rg: true cuando el submenu activo fue abierto con un clic. Mientras este fijo, el mouse (hover,
   * salir del menu) no lo cierra: solo otro clic, clic fuera, Escape o elegir una opcion.
   */
  #pinned = false;

  /** @param {MouseEvent} event */
  #clickListener = (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('[ref="menuitem"]');
    if (!link || !this.contains(link)) return;
    const submenu = findSubmenu(link);
    // Sin submenu: navega normal
    if (!submenu) return;
    // Con ctrl/cmd/shift/clic medio se deja abrir el enlace como siempre
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    event.preventDefault();
    this.#cancelScheduledAction();
    const li = link.closest('li') || link;
    if (this.#pinned && this.#state.activeItem === link) {
      this.#pinned = false;
      this.#deactivate();
      return;
    }
    // Si habia otro submenu abierto, se cambia al de este link
    this.#pinned = false;
    if (this.#state.activeItem && this.#state.activeItem !== link) this.#deactivate();
    this.#commitActivate(link, /** @type {any} */ ({ target: li }));
    this.#pinned = true;
  };

  /** @param {MouseEvent} event */
  #outsideClickListener = (event) => {
    if (!this.#pinned) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#pinned = false;
    this.#deactivate();
  };

  /** @param {KeyboardEvent} event */
  #escListener = (event) => {
    if (event.key !== 'Escape' || !this.#pinned) return;
    this.#pinned = false;
    this.#deactivate();
  };

  #menuPointerLeaveListener = () => {
    if (this.#pinned) return;
    if (!this.#state.activeItem) return;
    this.#scheduleAction(() => this.#deactivate());
  };

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * rg: temporizador compartido que retrasa un poco cada cambio de item (abrir o
   * cerrar). Sin esto, mover el mouse en diagonal desde un link del menu hacia un
   * producto del mega-menu cruza por encima de los links vecinos (Balones de
   * futbol, Ropa y accesorios...) en el camino, y cada cruce dispara activate()
   * sobre ese vecino, que cierra de inmediato el submenu que el cliente en
   * realidad queria seguir viendo -- el problema clasico de "safe triangle" en
   * menus desplegables. Con el retraso, un cruce rapido (menos de 150ms sobre el
   * vecino) nunca llega a confirmarse: el siguiente evento lo cancela antes de
   * que se ejecute, y el submenu original se mantiene abierto hasta que el mouse
   * de verdad se queda quieto en otro lado.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #actionTimeout = null;

  /**
   * @param {() => void} run
   */
  #scheduleAction(run) {
    if (this.#actionTimeout) clearTimeout(this.#actionTimeout);
    this.#actionTimeout = setTimeout(() => {
      this.#actionTimeout = null;
      run();
    }, 250);
  }

  #cancelScheduledAction() {
    if (this.#actionTimeout) {
      clearTimeout(this.#actionTimeout);
      this.#actionTimeout = null;
    }
  }

  /**
   * Activate the selected menu item after a short delay (see #scheduleAction)
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    // rg: mientras haya un submenu fijo por clic, el hover no lo cambia; y los items con submenu
    // propio solo se abren con clic (el hover los abria y los cerraba al ir a elegir una opcion).
    if (this.#pinned) return;
    if (event.type === 'pointerenter' && event.target.slot === '' && findSubmenu(item)) return;

    this.#scheduleAction(() => this.#commitActivate(item, event));
  };

  /**
   * @param {Element} item
   * @param {PointerEvent | FocusEvent} event
   */
  #commitActivate = (item, event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    // rg (05-10): el alto del fondo se calcula una sola vez al abrir; si en ese momento el header
    // o el submenu aun no tenian su alto final, el fondo oscuro quedaba mas corto que la lista y la
    // ultima opcion se veia blanca sobre blanco. Se remide con el submenu ya pintado.
    requestAnimationFrame(() => requestAnimationFrame(this.#fitHeaderToSubmenu));
    setTimeout(this.#fitHeaderToSubmenu, 350);
  };

  #fitHeaderToSubmenu = () => {
    const item = this.#state.activeItem;
    const header = this.headerComponent;
    if (!item || !header) return;
    const sm = findSubmenu(item);
    const inner = sm?.querySelector('.menu-list__submenu-inner') || sm;
    if (!inner || inner.offsetHeight === 0) return;
    const needed = Math.ceil(inner.getBoundingClientRect().bottom - header.getBoundingClientRect().top);
    const current = parseFloat(header.style.getPropertyValue('--full-open-header-height')) || 0;
    if (needed > current + 1) header.style.setProperty('--full-open-header-height', `${needed}px`);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;
    if (this.#pinned) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      // rg: si el mouse va hacia el propio submenu (o el overflow), cancelamos
      // cualquier apertura/cierre que hubiera quedado pendiente de un cruce
      // anterior, para no arriesgarnos a cerrar el submenu que el cliente
      // efectivamente esta por usar.
      this.#cancelScheduledAction();
      return;
    }

    this.#scheduleAction(() => this.#deactivate());
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.#pinned) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
